'use client';

import React from 'react';

/**
 * Toast Factory - Creates toast systems with configurable options
 *
 * Generic toast system factory that can be configured for different use cases.
 *
 * Usage:
 * ```typescript
 * const { toast, useToast } = createToastSystem<MyToastProps>({ limit: 1 });
 * ```
 */

// Generic toast props that all toast types must satisfy
export interface BaseToastProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  className?: string;
}

// Generic toast with required system fields
type BaseToasterToast<T extends BaseToastProps> = T & {
  id: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

// Action types for the reducer
type Action<T extends BaseToastProps> =
  | { type: 'ADD_TOAST'; toast: BaseToasterToast<T> }
  | {
      type: 'UPDATE_TOAST';
      toast: Partial<BaseToasterToast<T>> & { id: string };
    }
  | { type: 'DISMISS_TOAST'; toastId?: string | undefined }
  | { type: 'REMOVE_TOAST'; toastId?: string | undefined };

interface State<T extends BaseToastProps> {
  toasts: BaseToasterToast<T>[];
}

interface ToastSystemConfig {
  /** Maximum number of toasts to show at once */
  limit?: number;
  /** Delay before removing dismissed toasts (ms) */
  removeDelay?: number;
  /** Default auto-dismiss duration (ms). Set to 0 to disable auto-dismiss */
  defaultDuration?: number;
  /** ID generation strategy */
  idGenerator?: 'counter' | 'random';
}

const DEFAULT_CONFIG: Required<ToastSystemConfig> = {
  limit: 1,
  removeDelay: 1000000,
  defaultDuration: 0, // No auto-dismiss by default
  idGenerator: 'counter',
};

/**
 * Creates a toast system with the given configuration
 *
 * @param config - Configuration options for the toast system
 * @returns Object with toast function and useToast hook
 */
export function createToastSystem<T extends BaseToastProps>(
  config: ToastSystemConfig = {}
) {
  const { limit, removeDelay, defaultDuration, idGenerator } = {
    ...DEFAULT_CONFIG,
    ...config,
  };

  // State management
  const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
  const listeners: Array<(state: State<T>) => void> = [];
  let memoryState: State<T> = { toasts: [] };

  // ID generation
  let counter = 0;
  function genId(): string {
    if (idGenerator === 'random') {
      return Math.random().toString(36).substr(2, 9);
    }
    counter = (counter + 1) % Number.MAX_SAFE_INTEGER;
    return counter.toString();
  }

  // Dispatch function
  function dispatch(action: Action<T>) {
    memoryState = reducer(memoryState, action);
    listeners.forEach((listener) => {
      listener(memoryState);
    });
  }

  // Add to remove queue
  function addToRemoveQueue(toastId: string) {
    if (toastTimeouts.has(toastId)) {
      return;
    }

    const timeout = setTimeout(() => {
      toastTimeouts.delete(toastId);
      dispatch({ type: 'REMOVE_TOAST', toastId });
    }, removeDelay);

    toastTimeouts.set(toastId, timeout);
  }

  // Reducer
  function reducer(state: State<T>, action: Action<T>): State<T> {
    switch (action.type) {
      case 'ADD_TOAST':
        return {
          ...state,
          toasts: [action.toast, ...state.toasts].slice(0, limit),
        };

      case 'UPDATE_TOAST':
        return {
          ...state,
          toasts: state.toasts.map((t) =>
            t.id === action.toast.id ? { ...t, ...action.toast } : t
          ),
        };

      case 'DISMISS_TOAST': {
        const { toastId } = action;

        if (toastId) {
          addToRemoveQueue(toastId);
        } else {
          state.toasts.forEach((toast) => {
            addToRemoveQueue(toast.id);
          });
        }

        return {
          ...state,
          toasts: state.toasts.map((t) =>
            t.id === toastId || toastId === undefined
              ? { ...t, open: false }
              : t
          ),
        };
      }

      case 'REMOVE_TOAST':
        if (action.toastId === undefined) {
          return { ...state, toasts: [] };
        }
        return {
          ...state,
          toasts: state.toasts.filter((t) => t.id !== action.toastId),
        };
    }
  }

  // Toast function
  function toast(props: T & { duration?: number }) {
    const { duration = defaultDuration, ...toastProps } = props;
    const id = genId();

    const update = (updateProps: Partial<BaseToasterToast<T>>) =>
      dispatch({
        type: 'UPDATE_TOAST',
        toast: { ...updateProps, id },
      });

    const dismiss = () => dispatch({ type: 'DISMISS_TOAST', toastId: id });

    dispatch({
      type: 'ADD_TOAST',
      toast: {
        ...toastProps,
        id,
        open: true,
        onOpenChange: (open) => {
          if (!open) dismiss();
        },
      } as BaseToasterToast<T>,
    });

    // Auto-dismiss if duration is set
    if (duration > 0) {
      setTimeout(() => {
        dismiss();
      }, duration);
    }

    return { id, dismiss, update };
  }

  // Hook
  function useToast() {
    const [state, setState] = React.useState<State<T>>(memoryState);

    React.useEffect(() => {
      listeners.push(setState);
      return () => {
        const index = listeners.indexOf(setState);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      };
    }, [state]);

    return {
      ...state,
      toast,
      dismiss: (toastId?: string) =>
        dispatch({ type: 'DISMISS_TOAST', toastId }),
    };
  }

  return { toast, useToast, reducer };
}
